import React, { useEffect } from 'react';
import {
  helpContent,
  SOLUTIONS_CONTACT_EMAIL,
  SOLUTIONS_LINKEDIN_URL,
} from '../../shared/config/solutionsContent';
import { marketingSeo } from '../../shared/config/marketingSeo';
import { MarketingStaticHeader } from '../../components/marketing/solutions/MarketingStaticHeader';
import {
  SolutionsFaqSection,
  SolutionsFooter,
  SolutionsSecuritySection,
} from '../../components/marketing/solutions/SolutionsTrustSections';

/**
 * Public Help Center (/help) — full FAQ, security & data details, contact.
 *
 * Fully presentational (native <details> FAQ, plain anchors), so the SAME
 * component serves the SPA route and the build-time prerender
 * (scripts/prerender-solutions.mjs → build/help/index.html) — unlike
 * Solutions, there is no interactive twin to diverge. The title effect does
 * not run under renderToString, so Node prerendering is safe.
 *
 * Reuses the Solutions design system via the solutions-page--relix class
 * scope; copy lives in `solutionsContent.ts` (helpContent, solutionsFaq).
 */
export function HelpPage({
  showStaticHeader = false,
}: {
  /** Prerendered twin only — the SPA shows the Layout Navigation instead. */
  showStaticHeader?: boolean;
}): React.ReactElement {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = marketingSeo.help.title;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="solutions-page solutions-page--minimal solutions-page--relix help-page">
      {showStaticHeader ? <MarketingStaticHeader /> : null}
      <section className="help-hero" aria-labelledby="help-heading">
        <header className="solutions-section-header solutions-section-header--centered">
          <span className="solutions-section-eyebrow">{helpContent.eyebrow}</span>
          <h1 id="help-heading">{helpContent.heading}</h1>
          <p className="solutions-section-sub">{helpContent.sub}</p>
        </header>
      </section>

      <SolutionsFaqSection full />

      <SolutionsSecuritySection variant="cards" sectionId="security" privacyId="privacy" />

      <section className="help-contact" aria-labelledby="help-contact-heading">
        <header className="solutions-section-header solutions-section-header--centered">
          <span className="solutions-section-eyebrow">{helpContent.contactEyebrow}</span>
          <h2 id="help-contact-heading">{helpContent.contactHeading}</h2>
          <p className="solutions-section-sub">{helpContent.contactBody}</p>
        </header>
        <div className="help-contact__grid">
          <a className="help-contact__card" href={`mailto:${SOLUTIONS_CONTACT_EMAIL}`}>
            <span className="help-contact__card-label">Email</span>
            <span className="help-contact__card-value">{helpContent.contactEmailLabel}</span>
          </a>
          <a
            className="help-contact__card"
            href={SOLUTIONS_LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="help-contact__card-label">LinkedIn</span>
            <span className="help-contact__card-value">{helpContent.contactLinkedinLabel}</span>
          </a>
        </div>
      </section>

      <SolutionsFooter />
    </div>
  );
}
